/**
 * @module Filters
 * Functions for manipulating querystring objects with Conversation custom filters
 * the idea is to use as few reserved characters as possible to keep query strings readable
 * https://tools.ietf.org/html/rfc3986#section-2.3
 */

import moment from 'moment';

/**
 * @typedef {{key: string, operator: string, value: any[]}} Filter
 */

/**
 * @typedef {{[key: string]: string | string[]}} QuerystringObject
 */

/**
 * @constant
 * A map of Conversation custom filter key|operators to querystring safe keys(i.e. no reserved characters)
 */
const QUERYSTRING_SAFE_KEYS = {
  'status|choice:is': 'status.is',
  'status|choice:is not': 'status.isNot',
  'source|choice:is': 'source.is',
  'source|choice:is not': 'source.isNot',
  'agent|custom:agent is': 'agent.is',
  'agent|custom:agent is not': 'agent.isNot',
  'team|custom:team is': 'team.is',
  'team|custom:team is not': 'team.isNot',
  'bot|custom:bot is': 'bot.is',
  'bot|custom:bot is not': 'bot.isNot',
  'lastChannel|custom:last channel is': 'lastChannel.is',
  'lastChannel|custom:last channel is not': 'lastChannel.isNot',
  'tag|custom:tag is': 'tag.is',
  'tag|custom:tag is not': 'tag.isNot',
  'startedAt|date:is before': 'startedAt.isBefore',
  'startedAt|date:is': 'startedAt.is',
  'startedAt|date:is after': 'startedAt.isAfter',
  'startedAt|daterange:is between': 'startedAt.isBetween',
};

/**
 * Takes a filter and returns a key that is safe to use in a querystring
 * @param {Filter} filter
 * @returns {string} a querystring safe key
 */
const filterToQueryStringSafeKey = (filter) => {
  const { key, operator } = filter;
  const compoundKey = `${key}|${operator}`;
  return QUERYSTRING_SAFE_KEYS[compoundKey];
};

const timestampTranscoder = {
  toQueryStringValue: (timestamp) =>
    moment(timestamp, 'X').format('YYYY-MM-DD'),
  fromQueryStringValue: (querystringValue) =>
    moment(querystringValue, 'YYYY-MM-DD').format('X'),
};

const dateRangeTranscoder = {
  toQueryStringValue: (value) => {
    const { from, to } = value;
    const fromQuerystringValue = timestampTranscoder.toQueryStringValue(from);
    const toQuerystringValue = timestampTranscoder.toQueryStringValue(to);
    return `${fromQuerystringValue}_and_${toQuerystringValue}`;
  },
  fromQueryStringValue: (querystringValue) => {
    const [fromQuerystringValue, toQuerystringValue] =
      querystringValue.split('_and_');
    const from = timestampTranscoder.fromQueryStringValue(fromQuerystringValue);
    const to = timestampTranscoder.fromQueryStringValue(toQuerystringValue);
    return { from, to };
  },
};

const defaultTranscoder = {
  toQueryStringValue: (value) =>
    Array.isArray(value) ? value.join('_or_') : value,
  fromQueryStringValue: (querystringValue) =>
    String(querystringValue).split('_or_'),
};

const TIMESTAMP_OPERATORS = ['date:is', 'date:is after', 'date:is before'];
const TIMESTAMP_RANGE_OPERATORS = ['daterange:is between'];
const getTranscoder = (operator) => {
  if (TIMESTAMP_OPERATORS.includes(operator)) {
    return timestampTranscoder;
  }
  if (TIMESTAMP_RANGE_OPERATORS.includes(operator)) {
    return dateRangeTranscoder;
  }
  return defaultTranscoder;
};

/**
 * Takes a filter and returns a value that is safe to use in a querystring
 * @param {Filter} filter
 * @returns {string} a querystring safe value
 */
const filterToQueryStringSafeValue = (filter) => {
  const { operator, value } = filter;
  const transcoder = getTranscoder(operator);
  return transcoder.toQueryStringValue(value);
};

/**
 * Takes an array of filters and returns an object that is safe to be converted into a querystring
 * @param {Filter[]} filters
 * @returns {QuerystringObject} a querystring safe object
 */
export function filtersToQueryStringObject(filters) {
  return filters.reduce((queryStringObject, filter) => {
    const key = filterToQueryStringSafeKey(filter);
    const value = filterToQueryStringSafeValue(filter);
    if (queryStringObject[key]) {
      return {
        ...queryStringObject,
        [key]: [...queryStringObject[key], value],
      };
    }
    return { ...queryStringObject, [key]: [value] };
  }, {});
}

const queryStringValueToFilters = (queryStringValue, key, operator) => {
  const transcoder = getTranscoder(operator);

  const valueToFilter = (queryStringValue) => {
    return {
      key,
      operator,
      value: transcoder.fromQueryStringValue(queryStringValue),
    };
  };

  if (Array.isArray(queryStringValue)) {
    return queryStringValue.map(valueToFilter);
  }

  return [valueToFilter(queryStringValue)];
};

/**
 * Takes an object(intended to have come from a querystring) and extracts any custom filters
 * @param {QuerystringObject} queryStringObject
 * @returns {Filter[]} extracted filters
 */
export function filtersFromQueryStringObject(queryStringObject) {
  const pairedFilterAndQueryStringKeys = Object.entries(QUERYSTRING_SAFE_KEYS);
  return pairedFilterAndQueryStringKeys.reduce(
    (filters, [filterKey, queryStringKey]) => {
      const queryStringValue = queryStringObject[queryStringKey];

      if (queryStringValue === undefined) {
        return filters;
      }

      const [key, operator] = filterKey.split('|');
      const newFilters = queryStringValueToFilters(
        queryStringValue,
        key,
        operator
      );
      return [...filters, ...newFilters];
    },
    []
  );
}

/**
 * Takes an object(intended to have come from a querystring) and removes any custom filters from it
 * @param {QuerystringObject} queryStringObject
 * @returns {QuerystringObject} an object without custom filters
 */
export function removeFiltersFromQueryStringObject(queryStringObject) {
  return Object.values(QUERYSTRING_SAFE_KEYS).reduce(
    (queryStringObject, queryStringKey) => {
      delete queryStringObject[queryStringKey];
      return queryStringObject;
    },
    queryStringObject
  );
}

/**
 * Takes an object(intended to have come from a querystring) and an array of filters and adds the filters to the object
 * @param {QuerystringObject} queryStringObject
 * @param {Filters[]} filters
 * @returns {QuerystringObject} an object with the filters added
 */
export function addFiltersToQueryStringObject(
  initialQueryStringObject,
  filters
) {
  const queryStringObjectEntries = Object.entries(
    filtersToQueryStringObject(filters)
  );
  const output = queryStringObjectEntries.reduce(
    (currentQueryStringObject, [key, value]) => {
      currentQueryStringObject[key] = value;
      return currentQueryStringObject;
    },
    initialQueryStringObject
  );

  return output;
}
